import { useKeycloak } from '@react-keycloak/web';
import { Dispatch, Ref, SetStateAction, forwardRef, useEffect, useMemo, useState } from 'react';
import { useContext } from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Divider, Stack } from '@mui/material';

import { zodResolver } from '@hookform/resolvers/zod/dist/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import z from 'zod';
import { useShallow } from 'zustand/react/shallow';

import {
  AdvertisingPackageResponse,
  ClientResponse,
  CompanyResponse,
  MediaPlanResponse,
  MediaPlanWithFiles,
  PageResponseClientListItemResponse,
  SpaceResponse,
} from '@/api/data-contracts';
import { ReactComponent as BackIcon } from '@/assets/icons/back-icon.svg';
import { ReactComponent as ShoppingCartIcon } from '@/assets/icons/cart-icon.svg';
import { ROUTES } from '@/config/routes';
import { useClientsService, useCompaniesListQuery } from '@/features/company';
import { useMediaPlanForm } from '@/features/media-plans';
import { useMediaPlansService } from '@/features/media-plans';
import { filterValuesToPackageRequest } from '@/features/packages';
import { filterValuesToSpacesRequest } from '@/features/spaces';
import { useUsersService } from '@/features/users';
import { queryKeys } from '@/react-query/constants';
import { CURRENCY_SYMBOL } from '@/shared/constants';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import AppButton from '@/shared/ui/AppButton';
import useGeneratikTableStore from '@/shared/ui/GeneratikTable/generatikTableStore';
import RhfAutocomplete from '@/shared/ui/form-fields/RhfAutocomplete';
import RhfDateRangePicker from '@/shared/ui/form-fields/RhfDateRangePicker';
import RhfTextField from '@/shared/ui/form-fields/RhfTextField';

interface Props {
  handleClose: () => void;
  setActiveStep: Dispatch<SetStateAction<number>>;
}

const ShoppingCartNewMediaPlanForm = forwardRef(function ShoppingCartNewMediaPlanForm(
  { handleClose, setActiveStep }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const { t } = useTranslation();
  const userType = useContext(UserTypeContext);
  const { enqueueSnackbar } = useSnackbar();

  const { keycloak } = useKeycloak();
  const { getClients } = useClientsService();
  const navigate = useNavigate();
  const { FormSchema, mapSpaceToSpaceBooking, mapPackageToPackageBooking } = useMediaPlanForm();
  const { createMediaPlan, updateMediaPlanFilters } = useMediaPlansService();
  const { hasPermission } = useUsersService();

  const [organizationId, setOrganizationId] = useState<string | undefined>(undefined);
  const [hasPermissionToCreatePastBookings, setHasPermissionToCreatePastBookings] =
    useState<boolean>(false);

  useEffect(() => {
    const checkPermission = async () => {
      const permission = await hasPermission('create:past-bookings');
      setHasPermissionToCreatePastBookings(permission);
    };

    checkPermission();
  }, [hasPermission]);

  const {
    selectedSpaces,
    selectedPackages,
    startDate,
    endDate,
    setSelectedRows,
    spacesTableFilters,
    packagesTableFilters,
  } = useGeneratikTableStore(
    useShallow((state) => {
      const spacesTable = state.tables['ADVERTISING_SPACES'];
      const packagesTable = state.tables['ADVERTISING_PACKAGES'];

      const spacesDateRange = spacesTable?.filterValues?.dateRange as
        | { start?: string; end?: string }
        | undefined;
      const packagesDateRange = packagesTable?.filterValues?.dateRange as
        | { start?: string; end?: string }
        | undefined;

      const spacesStart = spacesDateRange?.start;
      const spacesEnd = spacesDateRange?.end;
      const packagesStart = packagesDateRange?.start;
      const packagesEnd = packagesDateRange?.end;

      const mediaPlanStartDate =
        spacesStart && packagesStart
          ? dayjs(spacesStart).isBefore(dayjs(packagesStart))
            ? spacesStart.split('T')[0]
            : packagesStart.split('T')[0]
          : (spacesStart?.split('T')[0] ?? packagesStart?.split('T')[0]);

      const mediaPlanEndDate =
        spacesEnd && packagesEnd
          ? dayjs(spacesEnd).isAfter(dayjs(packagesEnd))
            ? spacesEnd.split('T')[0]
            : packagesEnd.split('T')[0]
          : (spacesEnd?.split('T')[0] ?? packagesEnd?.split('T')[0]);

      return {
        selectedSpaces: (state.selectedRows['ADVERTISING_SPACES'] ?? []) as SpaceResponse[],
        selectedPackages: (state.selectedRows['ADVERTISING_PACKAGES'] ??
          []) as AdvertisingPackageResponse[],
        setSelectedRows: state.setSelectedRows,
        startDate: mediaPlanStartDate,
        endDate: mediaPlanEndDate,
        spacesTableFilters: spacesTable?.filterValues,
        packagesTableFilters: packagesTable?.filterValues,
      };
    }),
  );

  type FormSchemaType = z.infer<typeof FormSchema>;

  const methods = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  useEffect(() => {
    methods.setValue('request.startDate', startDate);
    methods.setValue('request.endDate', endDate);
  }, [endDate, startDate, methods]);

  const { mutateAsync: createMediaPlanMutation } = useMutation(
    (form: MediaPlanWithFiles) =>
      createMediaPlan({
        keyVisuals: [],
        request: {
          ...form.request,
          startDate: startDate ? `${startDate}T00:00:00Z` : undefined,
          endDate: endDate ? `${endDate}T20:59:59Z` : undefined,
          bookings: mapSpaceToSpaceBooking(selectedSpaces),
          packageBookings: mapPackageToPackageBooking(selectedPackages),
        },
      }),
    {
      onSuccess: async (response: MediaPlanResponse) => {
        setSelectedRows('ADVERTISING_SPACES', []);
        setSelectedRows('ADVERTISING_PACKAGES', []);

        const flatSpaceFilters = spacesTableFilters
          ? filterValuesToSpacesRequest(spacesTableFilters, userType)
          : {};
        const flatPackageFilters = packagesTableFilters
          ? filterValuesToPackageRequest(packagesTableFilters, userType)
          : {};

        await updateMediaPlanFilters(response.id, {
          spaceFilters: {
            ...flatSpaceFilters,
            start: flatSpaceFilters.start ?? (startDate ? `${startDate}T00:00:00Z` : undefined),
            end: flatSpaceFilters.end ?? (endDate ? `${endDate}T20:59:59Z` : undefined),
            sortColumn: 'id',
          },
          packageFilters: {
            ...flatPackageFilters,
            start: flatPackageFilters.start ?? (startDate ? `${startDate}T00:00:00Z` : undefined),
            end: flatPackageFilters.end ?? (endDate ? `${endDate}T20:59:59Z` : undefined),
            sortColumn: 'id',
          },
        });

        navigate(`${ROUTES.AUTHORIZED.MEDIA_PLANS}/${response.id}`);

        handleClose();
        setTimeout(() => {
          handleClose();
        }, 500);
      },
    },
  );

  const onSubmit: SubmitHandler<FormSchemaType> = async (form: FormSchemaType) => {
    createMediaPlanMutation(form);
  };

  const onError: SubmitErrorHandler<FormSchemaType> = () => {
    enqueueSnackbar(t('Please complete all required fields'), { variant: 'warning' });
  };

  useEffect(() => {
    if (keycloak.authenticated) {
      keycloak.loadUserInfo().then((userInfo: any) => {
        setOrganizationId(userInfo.organization);
      });
    }
  }, [keycloak]);

  const { data: clients } = useQuery<PageResponseClientListItemResponse>(
    [queryKeys.companyClients],
    ({ signal }) =>
      getClients(
        {
          request: {
            pageNumber: 0,
            numberOfItems: 1000,
            organizationId: organizationId!,
          },
        },
        signal,
      ),
    { enabled: !!organizationId },
  );

  const { data: companies } = useCompaniesListQuery({ pageNumber: 0, numberOfItems: 10000 });

  const clientId = useWatch({ control: methods.control, name: 'request.clientId' });

  const possibleBrands = useMemo(() => {
    if (!clientId || !clients?.content) return [];

    const client = clients.content?.find((client: ClientResponse) => client.id === clientId);

    return client?.brands || [];
  }, [clientId, clients?.content]);

  useEffect(() => {
    if (companies?.content && companies.content.length === 1) {
      methods.setValue('request.companyId', companies.content[0].id);
    }
  }, [companies?.content, methods]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onError)} autoComplete="off">
        <Stack
          ref={ref}
          sx={{
            padding: '0.75rem 1rem',
          }}
        >
          <Stack alignItems="flex-start" sx={{ marginBottom: '0.5rem' }}>
            <AppButton
              variant="ghost"
              size="small"
              startIcon={<BackIcon />}
              onClick={() => setActiveStep(1)}
            >
              {t('Back')}
            </AppButton>

            <Divider flexItem sx={{ margin: '0.375rem 0 !important' }} />
          </Stack>

          <RhfTextField
            name="request.planName"
            label={t('plan_name')}
            isRequired
            inputProps={{
              'data-testid': 'plan-name-input',
            }}
          />
          <RhfAutocomplete
            name="request.companyId"
            label={t('company')}
            options={
              companies?.content?.map((company: CompanyResponse) => ({
                label: company.companyName,
                value: company.id,
              })) ?? []
            }
            data-testid="company-select"
          />

          <RhfAutocomplete
            name="request.clientId"
            label={t('client')}
            options={
              clients?.content?.map((client) => ({
                label: `${client.organizationName} - ${client.companyName}`,
                value: client.id,
              })) ?? []
            }
            onValueChange={() => methods.setValue('request.brands', [])}
            data-testid="client-select"
          />
          <RhfAutocomplete
            name="request.brands"
            label={t('brand')}
            options={
              possibleBrands?.map((brand) => ({
                label: brand.brandName,
                value: brand.brandName,
              })) ?? []
            }
            multiple
            disabled={!possibleBrands.length}
            placeholder={!possibleBrands.length ? t('select_client') : ''}
            data-testid="brand-select"
          />
          <RhfTextField
            name="request.budget"
            label={t('budget')}
            valueType="number"
            inputProps={{ type: 'number' }}
            InputProps={{
              endAdornment: CURRENCY_SYMBOL,
            }}
          />
          <RhfDateRangePicker
            nameFrom="request.startDate"
            nameTo="request.endDate"
            label={t('campaign_duration')}
            disablePast={!hasPermissionToCreatePastBookings}
          />

          <AppButton
            variant="primary"
            size="medium"
            fullWidth
            startIcon={<ShoppingCartIcon />}
            disabled={!selectedSpaces.length && !selectedPackages.length}
            type="submit"
            data-testid="create-media-plan-button"
          >
            {t('Create Media Plan')}
          </AppButton>
        </Stack>
      </form>
    </FormProvider>
  );
});

export default ShoppingCartNewMediaPlanForm;

const DEFAULT_FORM_VALUES = {
  request: {
    planName: '',
    clientId: '',
    companyId: '',
    brands: [],
    budget: 0,
    startDate: '',
    endDate: '',
    bookings: [],
    packageBookings: [],
    filters: {},
  },
  KeyVisuals: [],
};
